import axios from 'axios';
import get from 'lodash/get';

const UserApi = {
  /**
   * Get data and list of fields and returns either a JSON object or an URL encoded string.
   * @param data Data which contains list of values.
   * @param fields Map of fields (key is source field name and value is target field name).
   * @param urlEncoded Defines whether the return value should be as URL encoded string or a JSON object.
   * @return {string | object}
   * @private
   */
  _initData (data, fields, urlEncoded = true) {
    let result = urlEncoded ? [] : {};
    for (const field in fields) {
      if (urlEncoded) {
        result.push(`${fields[field]}=${encodeURIComponent(get(data, field))}`);
      } else {
        result[fields[field]] = get(data, field);
      }
    }
    return urlEncoded ? result.join('&') : result;
  },

  /**
   * Register user.
   * @param data
   * @return {Promise<boolean>}
   */
  register (data) {
    const defaults = {
      titleCode: 'mr',
      firstName: '',
      lastName: '',
      email: '',
      pwd: '',
      checkPwd: '',
      optin: false,
      optinElectra: false,
      CSRFToken: 'CSRFToken'
    };

    const formData = Object.assign(defaults, data);
    const url = '/register';
    return axios({
      url,
      method: 'post',
      data: formData
    });
  },

  /**
   * Update user's profile.
   * @param data
   * @return {Promise<void>}
   */
  updateProfile (data) {
    const defaults = {
      titleCode: 'mr',
      firstName: '',
      lastName: '',
      CSRFToken: '',
    };

    const options = Object.assign(defaults, data);
    return axios({
      url: '/account/update-profile/',
      method: 'post',
      data: options,
    });
  },

  /**
   * Get list of available languages.
   * @return {AxiosPromise<any>}
   */
  retrieveAvailableLanguages () {
    return axios.get('/customer/profile/languages');
  },

  /**
   * Update user's preferred language.
   * @param preferredLanguage
   * @return {AxiosPromise<any>}
   */
  updateLanguage (preferredLanguage) {
    return axios
      .patch(
        '/customer/profile',
        {
          preferredLanguage,
        },
        {
          params: {
            prop: 'language',
          },
        }
      )
  },

  /**
   * Update user's password.
   * @param data
   * @return {Promise<void>}
   */
  updatePassword (data) {
    return axios({
      url: '/account/update-password/',
      method: 'post',
      data,
    });
  },

  /**
   * Update user's email address.
   * @param data
   * @return {Promise<void>}
   */
  updateEmailAddress (data) {
    const defaults = {
      password: '',
    };

    const options = Object.assign(defaults, data);
    return axios({
      url: '/account/update-email/',
      method: 'post',
      data: options
    });
  },

  /**
   * Recovery password.
   * @param apiUrl
   * @param data
   */
  recoveryUpdatePassword (apiUrl, data) {
    const defaults = {
      pwd: '',
      checkPwd: '',
      token: '',
    };

    let options = Object.assign(defaults, data);
    return axios({
      url: apiUrl,
      method: 'post',
      data: options
    });
  },

  /**
   * Perform a password recovery request.
   * @param data
   * @return {AxiosPromise}
   */
  passwordRecovery (data) {
    const defaults = {
      email: '',
      CSRFToken: ''
    };
    const options = Object.assign(defaults, data);
    return axios({
      url: '/login/pw/request/external/',
      method: 'post',
      data: options
    });
  },

  /**
   * Guest login.
   * @return {Promise<*>}
   */
  async guestLogin (data) {
    const fields = {
      email: 'email'
    };
    const formData = this._initData(data, fields, true);
    const url = '/login/checkout/guest';
    const response = await axios({
      url,
      method: 'post',
      withCredentials: true,
      crossorigin: true,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      data: formData
    });
    return get(response, 'request.responseURL', '');
  },
};

export {UserApi};
